import React from 'react';

import { Chip, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import type { ProcessingActivityDetail } from 'config/api/processingActivities/processingActivities.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import type { ProcessingActivityDetailViewModel } from '../processingActivityDetail.types';
import useStyles from '../ProcessingActivityDetailPage.styles';

type Props = {
  detail: ProcessingActivityDetail;
  viewModel: ProcessingActivityDetailViewModel;
};

const formatDate = (value?: string | null) => (value ? format(new Date(value), 'yyyy-MM-dd') : '—');

const getMessageFromMap = (map: Record<string, string[]>, code: string, t: (message: string | string[]) => string): string => {
  const message = map[code];
  return message ? t(message) : code;
};

const ProcessingActivityPublishedSection: React.FC<Props> = ({ detail, viewModel }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const processingRoleLabel = detail.processingRole
    ? getMessageFromMap(processing_activities_messages.processing_role, detail.processingRole, t)
    : t(processing_activities_messages.review_placeholder);

  const statusLabel = detail.status
    ? getMessageFromMap(processing_activities_messages.status, detail.status, t)
    : t(processing_activities_messages.status.draft);

  const renderCategoryChips = (categories: { id?: number; name: string }[]) => {
    if (!categories.length) {
      return <Typography className={styles.readValue}>{t(processing_activities_messages.review_placeholder)}</Typography>;
    }

    return (
      <div className={styles.tagRow}>
        {categories.map(category => (
          <Chip key={category.id ?? category.name} className={styles.categoryChip} label={category.name} size='small' />
        ))}
      </div>
    );
  };

  return (
    <div className={styles.panel}>
      <Typography className={styles.panelTitle} component='h2' variant='h6'>
        {t(processing_activities_messages.detail.sections.published)}
      </Typography>

      <div className={styles.readGrid}>
        <div className={clsx(styles.readField, styles.readFieldFull)}>
          <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.description)}</div>
          <div className={styles.readValue}>{detail.description || t(processing_activities_messages.review_placeholder)}</div>
        </div>
        <div className={styles.readField}>
          <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.processing_role)}</div>
          <div className={styles.readValue}>{processingRoleLabel}</div>
        </div>
        <div className={styles.readField}>
          <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.published_version)}</div>
          <div className={styles.readValue}>
            {detail.publishedVersionNumber != null
              ? t(processing_activities_messages.table.version_label, { number: detail.publishedVersionNumber })
              : t(processing_activities_messages.review_placeholder)}
          </div>
        </div>
        <div className={styles.readField}>
          <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.status)}</div>
          <div className={styles.readValue}>
            <span
              aria-hidden='true'
              className={clsx(styles.statusDot, {
                [styles.statusDotActive]: detail.status === 'published',
                [styles.statusDotInfo]: detail.status === 'archived',
                [styles.statusDotWarning]: !detail.status,
              })}
            />
            {statusLabel}
          </div>
        </div>
        <div className={styles.readField}>
          <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.last_published)}</div>
          <div className={styles.readValue}>{formatDate(detail.publishedUpdatedAt)}</div>
        </div>
      </div>

      <div className={styles.categoryBlock}>
        <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.data_subject_categories)}</div>
        {renderCategoryChips(viewModel.categories.dataSubjectCategories)}
      </div>

      <div className={styles.categoryBlock}>
        <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.personal_data_categories)}</div>
        {renderCategoryChips(viewModel.categories.personalDataCategories)}
      </div>

      <div className={styles.categoryBlock}>
        <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.recipient_categories)}</div>
        {renderCategoryChips(viewModel.categories.recipientCategories)}
      </div>

      <div className={clsx(styles.readField, styles.readFieldFull)}>
        <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.minimum_security_measures)}</div>
        <div className={styles.readValue}>{detail.minimumSecurityMeasures || t(processing_activities_messages.review_placeholder)}</div>
      </div>
    </div>
  );
};

export default ProcessingActivityPublishedSection;
